import {
  DepositPaymentMode,
  DepositRequestStatus,
  type IDepositRequest,
} from '@principle-theorem/principle-core/interfaces';
import {
  initMoment,
  Timestamp,
  toMoment,
  type AtLeast,
} from '@principle-theorem/shared';
import { duration, type Moment } from 'moment-timezone';

type DepositRequestOverrides = AtLeast<
  IDepositRequest,
  'practiceRef' | 'treatmentName' | 'amountCents' | 'currency'
>;

/**
 * How long an unpaid deposit request stays open before the expiry cron
 * releases it and unblocks the held slot. Capped at Stripe's 7-day
 * authorisation ceiling so a pre-auth can never outlive its request.
 */
export const DEPOSIT_REQUEST_HOLD = duration(7, 'days');
export const DEPOSIT_REQUEST_HOLD_MS = DEPOSIT_REQUEST_HOLD.asMilliseconds();

/** Owns construction and lifetime of a deposit request document. */
export class DepositRequest {
  /**
   * Seeds the fields every deposit request needs regardless of the booking
   * path it came from, so no caller can persist one without an expiry or a
   * starting status.
   */
  static init(overrides: DepositRequestOverrides): IDepositRequest {
    return {
      paymentMode: DepositPaymentMode.Charge,
      status: DepositRequestStatus.Pending,
      expiresAt: DepositRequest.resolveExpiry(),
      deleted: false,
      ...overrides,
    };
  }

  /**
   * Bounds the request by both the hold window and the appointment it is
   * holding, so an expired-but-open request can never keep a slot blocked past
   * the appointment itself.
   */
  static resolveExpiry(
    appointmentStart?: Timestamp,
    nowMillis: number = Date.now()
  ): Timestamp {
    const holdExpiryMillis = nowMillis + DEPOSIT_REQUEST_HOLD_MS;
    const appointmentStartMillis = appointmentStart?.toMillis();
    return Timestamp.fromMillis(
      appointmentStartMillis === undefined
        ? holdExpiryMillis
        : Math.min(holdExpiryMillis, appointmentStartMillis)
    );
  }

  /**
   * A request stops taking payment the moment `expiresAt` arrives, before the
   * expiry task has had a chance to mark it Expired.
   */
  static hasExpired(
    depositRequest: Pick<IDepositRequest, 'expiresAt'>,
    at: Moment = initMoment()
  ): boolean {
    return !toMoment(depositRequest.expiresAt).isAfter(at);
  }
}
